import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Avatar,
  Typography,
  Box,
  Chip,
  makeStyles,
  useTheme,
  Fade,
  InputAdornment,
  MenuItem
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";

// Ícones
import SearchIcon from "@material-ui/icons/Search";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import SupervisedUserCircle from "@material-ui/icons/SupervisedUserCircle";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import UserAvatar from "../UserAvatar";
import toastError from "../../errors/toastError";

const filterOptions = createFilterOptions({
  trim: true,
});

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    width: "min(360px, calc(100vw - 32px))",
    maxWidth: "calc(100vw - 32px)",
  },

  autocomplete: {
    marginTop: theme.spacing(3),
    marginBottom: 0,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: theme.palette.background.paper,
      height: 46,
      transition: theme.transitions.create(
        ["border-color", "box-shadow", "background-color"],
        { duration: theme.transitions.duration.shorter }
      ),
      "& fieldset": {
        borderColor: alpha(theme.palette.divider, 0.45),
        borderWidth: 1.5,
      },
      "&:hover fieldset": {
        borderColor: alpha(theme.palette.primary.main, 0.55),
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
      "& .MuiOutlinedInput-notchedOutline legend span": {
        paddingLeft: 4,
        paddingRight: 4,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "0 14px",
      fontSize: "0.9rem",
      lineHeight: "46px",
    },
    "& .MuiInputAdornment-positionStart": {
      marginTop: 0,
      alignSelf: "center",
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "1rem",
      fontWeight: 400,
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
  },

  autocompleteInputRoot: {
    "&&": {
      padding: "0 12px",
      minHeight: 46,
      alignItems: "center",
    },
  },

  autocompleteInput: {
    "&&": {
      padding: "0",
      lineHeight: "46px",
    },
  },

  queueField: {
    marginTop: theme.spacing(3),
    marginBottom: 0,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: theme.palette.background.paper,
      height: 46,
      transition: theme.transitions.create(
        ["border-color", "box-shadow", "background-color"],
        { duration: theme.transitions.duration.shorter }
      ),
      "& fieldset": {
        borderColor: alpha(theme.palette.divider, 0.45),
        borderWidth: 1.5,
      },
      "&:hover fieldset": {
        borderColor: alpha(theme.palette.primary.main, 0.55),
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
      "& .MuiOutlinedInput-notchedOutline legend span": {
        paddingLeft: 4,
        paddingRight: 4,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
      fontSize: "0.9rem",
      display: "flex",
      alignItems: "center",
    },
    "& .MuiSelect-iconOutlined": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "1rem",
      fontWeight: 400,
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
  },

  connectionField: {
    marginTop: theme.spacing(1.5),
    marginBottom: 0,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: theme.palette.background.paper,
      height: 46,
      transition: theme.transitions.create(
        ["border-color", "box-shadow", "background-color"],
        { duration: theme.transitions.duration.shorter }
      ),
      "& fieldset": {
        borderColor: alpha(theme.palette.divider, 0.45),
        borderWidth: 1.5,
      },
      "&:hover fieldset": {
        borderColor: alpha(theme.palette.primary.main, 0.55),
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
      "& .MuiOutlinedInput-notchedOutline legend span": {
        paddingLeft: 4,
        paddingRight: 4,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
      fontSize: "0.9rem",
      display: "flex",
      alignItems: "center",
    },
    "& .MuiSelect-iconOutlined": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "1rem",
      fontWeight: 400,
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
  },

  queuePlaceholder: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },

  connectionPlaceholder: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },

  listbox: {
    padding: 8,
    "& .MuiAutocomplete-option": {
      borderRadius: 8,
      margin: "2px 0",
      padding: "10px 12px",
      "&:hover": {
        backgroundColor: theme.palette.primary.main + "10",
      },
      '&[aria-selected="true"]': {
        backgroundColor: theme.palette.primary.main + "15",
      }
    }
  },

  userOption: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
  },

  avatar: {
    width: 40,
    height: 40,
    fontSize: "0.875rem",
    fontWeight: 700,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },

  userInfo: {
    flex: 1,
    overflow: "hidden",
  },

  userName: {
    fontWeight: 600,
    fontSize: "0.875rem",
    lineHeight: 1.4,
    color: theme.palette.text.primary,
  },

  userEmail: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  onlineChip: {
    height: 18,
    fontSize: "0.65rem",
    fontWeight: 700,
    backgroundColor: "#4caf50",
    color: "#fff",
  },

  selectedBox: {
    marginTop: 12,
    padding: 10,
    borderRadius: 8,
    backgroundColor: theme.palette.success.main + "10",
    border: `1.5px solid ${theme.palette.success.main}30`,
    display: "flex",
    alignItems: "center",
    gap: 8,
    animation: "$slideIn 0.3s ease",
  },

  "@keyframes slideIn": {
    from: {
      opacity: 0,
      transform: "translateY(-8px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    }
  },

  selectedIcon: {
    color: theme.palette.success.main,
    fontSize: 20,
  },

  selectedText: {
    fontSize: "0.75rem",
    color: theme.palette.text.primary,
    "& strong": {
      fontWeight: 700,
      color: theme.palette.success.main,
    }
  },

  dialogActions: {
    padding: theme.spacing(1.5, 2, 2),
    gap: theme.spacing(1),
    flexWrap: "nowrap",
  },

  cancelButton: {
    ...theme.neoUI.buttons.action.cancel.root,
    height: "32.5px",
    flex: 1,
    minWidth: 0,
  },

  transferButton: {
    ...theme.neoUI.buttons.action.root,
    height: "32.5px",
    flex: 1,
    minWidth: 0,
  },

  searchIcon: {
    color: theme.palette.text.disabled,
    fontSize: 20,
  },

  emptyIcon: {
    color: theme.palette.text.disabled,
    fontSize: 18,
  }
}));

const todosOption = {
  id: "todos",
  name: "Todos",
  email: "Ticket vísivel para todos",
  isShared: true,
  online: false
};

const TransferTicketModal = ({ modalOpen, onClose, ticketid }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [queues, setQueues] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [selectedQueueId, setSelectedQueueId] = useState("");
  const [connections, setConnections] = useState([]);
  const [connectionLoading, setConnectionLoading] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState("");

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: { searchParam },
          });

          const users = data.users;

          if ("todos".includes(searchParam.toLowerCase())) {
            users.unshift(todosOption);
          }

          setOptions(users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchUsers();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  useEffect(() => {
    if (!modalOpen) {
      return;
    }

    let isActive = true;

    const fetchQueues = async () => {
      setQueueLoading(true);
      try {
        const { data } = await api.get("/queue");
        if (isActive) {
          setQueues(data);
        }
      } catch (err) {
        if (isActive) {
          toastError(err);
        }
      } finally {
        if (isActive) {
          setQueueLoading(false);
        }
      }
    };

    const fetchConnections = async () => {
      setConnectionLoading(true);
      try {
        const { data } = await api.get("/whatsapp/?session=0");
        if (isActive) {
          setConnections(data);
        }
      } catch (err) {
        if (isActive) {
          toastError(err);
        }
      } finally {
        if (isActive) {
          setConnectionLoading(false);
        }
      }
    };

    fetchQueues();
    fetchConnections();

    return () => {
      isActive = false;
    };
  }, [modalOpen]);

  useEffect(() => {
    if (selectedQueueId === "") {
      return;
    }

    const exists = queues.some(queue => queue.id === selectedQueueId);
    if (!exists) {
      setSelectedQueueId("");
    }
  }, [queues, selectedQueueId]);

  useEffect(() => {
    if (selectedConnectionId === "") {
      return;
    }

    const exists = connections.some(connection => connection.id === selectedConnectionId);
    if (!exists) {
      setSelectedConnectionId("");
    }
  }, [connections, selectedConnectionId]);

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setSelectedUser(null);
    setOptions([]);
    setSelectedQueueId("");
    setSelectedConnectionId("");
  };

  const handleSaveTicket = async e => {
    e.preventDefault();
    if (!ticketid) return;

    // Verificar se pelo menos um campo está preenchido
    if (!selectedUser && selectedQueueId === "" && selectedConnectionId === "") {
      return;
    }

    setLoading(true);
    try {
      const updateData = {
        status: "open"
      };

      if (selectedUser) {
        if (selectedUser.isShared) {
          updateData.userId = null;
          updateData.isShared = true;
        } else {
          updateData.userId = selectedUser.id;
        }
      }

      if (selectedQueueId !== "") {
        updateData.queueId = selectedQueueId;
      }

      if (selectedConnectionId !== "") {
        updateData.whatsappId = selectedConnectionId;
      }

      const { data: updatedTicket } = await api.put(`/tickets/${ticketid}`, updateData);
      if (typeof window !== "undefined" && updatedTicket?.id) {
        window.dispatchEvent(
          new CustomEvent("ticket:patched", { detail: { ticket: updatedTicket } })
        );
      }
      setLoading(false);
      handleClose();

      if (updatedTicket?.wasTransferred && updatedTicket?.id) {
        history.push(`/tickets/${updatedTicket.id}`);
      } else {
        history.push(`/tickets`);
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const renderUserOption = (option) => (
    <div className={classes.userOption}>
      {option.isShared ? (
        <Avatar
          className={classes.avatar}
          style={{
            backgroundColor: "#e8f5ee",
            border: theme.palette.type === "dark" ? "2px solid #1c1c1c" : undefined,
          }}
        >
          <SupervisedUserCircle
            style={{
              color: "#14A45C",
              width: "125%",
              height: "125%",
            }}
          />
        </Avatar>
      ) : (
        <UserAvatar user={option} className={classes.avatar} />
      )}
      <div className={classes.userInfo}>
        <Typography className={classes.userName}>
          {option.name}
          {option.online && (
            <Chip
              label="Online"
              className={classes.onlineChip}
              style={{ marginLeft: 8 }}
            />
          )}
        </Typography>
        <Typography className={classes.userEmail}>
          {option.email}
        </Typography>
      </div>
    </div>
  );

  return (
    <Dialog
      open={modalOpen}
      onClose={handleClose}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 400 }}
      PaperProps={{ className: classes.dialogPaper }}
      aria-labelledby="transfer-ticket-custom-title"
    >
      <form onSubmit={handleSaveTicket}>
        <DialogTitle id="transfer-ticket-custom-title">
          {i18n.t("transferTicketModal.title")}
        </DialogTitle>

        <DialogContent>
          <TextField
            select
            fullWidth
            variant="outlined"
            className={classes.connectionField}
            label={i18n.t("transferTicketModal.fieldConnectionLabel")}
            placeholder={i18n.t("transferTicketModal.fieldConnectionPlaceholder")}
            value={selectedConnectionId}
            onChange={event => {
              const connectionId = event.target.value;
              setSelectedConnectionId(connectionId === "" ? "" : Number(connectionId));
            }}
            SelectProps={{
              displayEmpty: true,
              renderValue: value => {
                if (value === "" || value === undefined) {
                  return (
                    <span className={classes.connectionPlaceholder}>
                      {i18n.t("transferTicketModal.fieldConnectionPlaceholder")}
                    </span>
                  );
                }
                const connection = connections.find(item => item.id === Number(value));
                return connection ? connection.name : "";
              }
            }}
            disabled={connectionLoading}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">
              <Typography color="textSecondary">
                {i18n.t("transferTicketModal.fieldConnectionPlaceholder")}
              </Typography>
            </MenuItem>
            {connections.map(connection => (
              <MenuItem key={connection.id} value={connection.id}>
                {connection.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            variant="outlined"
            className={classes.queueField}
            label={i18n.t("transferTicketModal.fieldQueueLabel")}
            placeholder={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
            value={selectedQueueId}
            onChange={event => {
              const queueId = event.target.value;
              setSelectedQueueId(queueId === "" ? "" : Number(queueId));
            }}
            SelectProps={{
              displayEmpty: true,
              renderValue: value => {
                if (value === "" || value === undefined) {
                  return (
                    <span className={classes.queuePlaceholder}>
                      {i18n.t("transferTicketModal.fieldQueuePlaceholder")}
                    </span>
                  );
                }
                const queue = queues.find(item => item.id === Number(value));
                return queue ? queue.name : "";
              }
            }}
            disabled={queueLoading}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">
              <Typography color="textSecondary">
                {i18n.t("transferTicketModal.fieldQueuePlaceholder")}
              </Typography>
            </MenuItem>
            {queues.map(queue => (
              <MenuItem key={queue.id} value={queue.id}>
                {queue.name}
              </MenuItem>
            ))}
          </TextField>

          <Autocomplete
            className={classes.autocomplete}
            classes={{
              listbox: classes.listbox,
              inputRoot: classes.autocompleteInputRoot,
              input: classes.autocompleteInput,
            }}
            getOptionLabel={option => option.name || ""}
            value={selectedUser}
            onChange={(e, newValue) => setSelectedUser(newValue)}
            options={options}
            filterOptions={filterOptions}
            freeSolo
            autoHighlight
            noOptionsText={
              <Box display="flex" alignItems="center" gap={1} p={1}>
                <PersonOutlineIcon className={classes.emptyIcon} />
                <Typography variant="body2" color="textSecondary">
                  {searchParam.length < 3
                    ? "Digite pelo menos 3 caracteres"
                    : i18n.t("transferTicketModal.noOptions")
                  }
                </Typography>
              </Box>
            }
            loading={loading}
            renderOption={renderUserOption}
            renderInput={params => (
              <TextField
                {...params}
                label={i18n.t("transferTicketModal.fieldLabel")}
                placeholder="Buscar por nome..."
                variant="outlined"
                autoFocus
                fullWidth
                onChange={e => setSearchParam(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon className={classes.searchIcon} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
                InputLabelProps={{
                  ...params.InputLabelProps,
                  shrink: true,
                }}
              />
            )}
          />

          {selectedUser && (
            <Fade in={true}>
              <Box className={classes.selectedBox}>
                <CheckCircleOutlineIcon className={classes.selectedIcon} />
                <Typography className={classes.selectedText}>
                  Transferindo para <strong>{selectedUser.name}</strong>
                </Typography>
              </Box>
            </Fade>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleClose}
            disabled={loading}
            variant="outlined"
            className={classes.cancelButton}
          >
            {i18n.t("transferTicketModal.buttons.cancel")}
          </Button>
          <ButtonWithSpinner
            variant="contained"
            type="submit"
            color="primary"
            loading={loading}
            disabled={!selectedUser && selectedQueueId === "" && selectedConnectionId === ""}
            className={classes.transferButton}
          >
            Transferir
          </ButtonWithSpinner>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TransferTicketModal;
